// Council state: the session, who sits / is muted, the transcript, voices (auto-speak queue) and the
// hands-free ear. UI-free, so the Pantheon's COUNCIL mode (SelfView) and the settings page share it.
//
// A turn = the user's line → the server picks who answers → each speaker answers in order, hearing
// the last; a deity that @-tags another member pulls them into the round (Bothread-style mentions).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { notifyDataChanged } from '../live/screenContext';
import { useSettingColumn } from '../olmps/settings/useSettingColumn';
import { SEAT_BY_ID } from '../v10/roster';
import { councilAudio, type PlaybackState } from './councilAudio';
import {
  DEFAULT_COUNCIL,
  councilSpeak,
  councilTurn,
  createCouncilSession,
  deleteCouncilSession,
  updateCouncilSession,
  type CouncilStatus,
  listCouncilSessions,
  loadCouncilLines,
  saveCouncilRoster,
  withCouncilDefaults,
  type CouncilLine,
  type CouncilSession,
  type CouncilSettings,
} from './councilApi';
import { useCouncilEar } from './useCouncilEar';

/** Five podiums. members[i] is who stands on podium i ('' = empty). */
export const SLOTS = 5;
const padSlots = (m: string[]) => Array.from({ length: SLOTS }, (_x, i) => m[i] ?? '');
const seatedOf = (m: string[]) => m.filter(Boolean);

export const seatName = (id: string) => (id === 'syqi' ? 'SyQi' : id === 'user' ? 'You' : SEAT_BY_ID[id]?.name ?? id.toUpperCase());

export function useCouncil({ active, initialMembers, topic, roomName, alwaysSpeak = false, onWrote }: { active: boolean; initialMembers?: string[]; topic?: string; /** Title for a session this hook creates (default: "Council <date>"). */ roomName?: string; /** Always read replies aloud, whatever the Conference setting. */ alwaysSpeak?: boolean; /** Called when a deity wrote data, INSTEAD of the app-wide notifyDataChanged (which remounts the open tool and would wipe a room that lives inside it). */ onWrote?: (w: { sessionId: string; speaker: string; since: string }) => void }) {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const settingsCol = useSettingColumn<Partial<CouncilSettings> | null>(userId, 'council_settings', null);
  const settings = withCouncilDefaults(settingsCol.value);

  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [name, setName] = useState('');
  const [status, setStatus] = useState<CouncilStatus>('active');
  const [members, setMembers] = useState<string[]>(padSlots(initialMembers?.length ? initialMembers : DEFAULT_COUNCIL.defaultMembers));
  const [muted, setMuted] = useState<string[]>([]);
  const [lines, setLines] = useState<CouncilLine[]>([]);
  const [thinking, setThinking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState<CouncilSession[]>([]);
  const [play, setPlay] = useState<PlaybackState>(councilAudio.current);
  const [conference, setConference] = useState(true);
  const pending = useRef<string[]>([]);
  const alive = useRef(true);
  const sessionIdRef = useRef<string | undefined>(undefined);
  sessionIdRef.current = sessionId;
  const stopRef = useRef(false);
  const onWroteRef = useRef(onWrote);
  onWroteRef.current = onWrote;
  const wroteData = (w: { sessionId: string; speaker: string; since: string }) => (onWroteRef.current ? onWroteRef.current(w) : notifyDataChanged());
  const membersRef = useRef(members);
  membersRef.current = members;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  // Settings seed the defaults once loaded (members only when nobody was named on the way in).
  const seeded = useRef(false);
  useEffect(() => {
    if (settingsCol.loading || seeded.current) return;
    seeded.current = true;
    setConference(alwaysSpeak || settings.autoSpeak);
    if (!initialMembers?.length && settings.defaultMembers.length) setMembers(padSlots(settings.defaultMembers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsCol.loading]);
  // A fresh request (SyQi named deities) replaces the roster.
  useEffect(() => {
    if (initialMembers?.length) setMembers(padSlots(initialMembers));
  }, [initialMembers]);

  useEffect(() => councilAudio.subscribe(setPlay), []);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      councilAudio.stop();
    };
  }, []);
  useEffect(() => {
    if (!active) councilAudio.stop();
  }, [active]);

  const speakingId = useMemo(() => (play.id ? lines.find((l) => l.id === play.id)?.speaker ?? null : null), [play.id, lines]);
  const append = (l: CouncilLine) => setLines((p) => [...p, l]);
  const note = (content: string) => append({ id: `note-${Date.now()}-${Math.random()}`, speaker: 'system', content, kind: 'system', created_at: new Date().toISOString() });

  const runSpeakers = useCallback(
    async (sid: string, speakers: string[]) => {
      const queue = [...speakers];
      const spoken = new Set<string>();
      let extra = 0;
      while (queue.length) {
        const sp = queue.shift()!;
        if (!alive.current) return;
        spoken.add(sp);
        setThinking(sp);
        try {
          const since = new Date(Date.now() - 5000).toISOString();
          const r = await councilSpeak(sid, sp);
          if (!alive.current) return;
          if (r.line) {
            append(r.line);
            if (conference) councilAudio.enqueue({ id: r.line.id, speaker: r.line.speaker, text: r.line.content });
          }
          if (r.wrote) wroteData({ sessionId: sid, speaker: sp, since });
          for (const m of r.mentions ?? []) {
            if (m && extra < 4 && !spoken.has(m) && !queue.includes(m) && membersRef.current.includes(m) && !mutedRef.current.includes(m)) {
              queue.push(m);
              extra++;
            }
          }
        } catch (e) {
          note(`${seatName(sp)} could not answer (${e instanceof Error ? e.message : 'error'}).`);
        }
      }
      setThinking(null);
    },
    [conference],
  );

  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text) return;
      if (busy) {
        pending.current.push(text);
        return;
      }
      setBusy(true);
      const temp: CouncilLine = { id: `tmp-${Date.now()}`, speaker: 'user', content: text, kind: 'message', created_at: new Date().toISOString() };
      append(temp);
      try {
        const r = await councilTurn({ sessionId: sessionIdRef.current, name: roomName, members: seatedOf(membersRef.current), muted: mutedRef.current, message: text });
        if (!alive.current) return;
        sessionIdRef.current = r.sessionId;
        setSessionId(r.sessionId);
        setLines((p) => p.map((l) => (l.id === temp.id ? r.line : l)));
        if (r.paused) note('Council paused. Resume it and the deities will answer.');
        else if (!r.speakers.length) note('Everyone seated is muted. Unmute someone or seat a deity.');
        await runSpeakers(r.sessionId, r.speakers);
      } catch (e) {
        note(`The council could not take that (${e instanceof Error ? e.message : 'error'}). Try again.`);
      } finally {
        if (alive.current) {
          setBusy(false);
          const next = pending.current.shift();
          if (next) void send(next);
        }
      }
    },
    [busy, roomName, runSpeakers],
  );
  const sendRef = useRef(send);
  sendRef.current = send;

  /** A back-and-forth between members, in `order`, without waiting for the user: each speaker hears the
   *  last. Stops after `maxTurns`, when `done(line)` is true, after two passes in a row, or on stop().
   *  Whatever the user types or says meanwhile is posted into the room before the next speaker. */
  const converse = useCallback(
    async (order: string[], maxTurns: number, done: (l: CouncilLine) => boolean = () => false) => {
      const sid = sessionIdRef.current;
      if (!sid || !order.length) return;
      stopRef.current = false;
      setBusy(true);
      let passes = 0;
      try {
        for (let turn = 0; turn < maxTurns && alive.current && !stopRef.current; turn++) {
          const interjection = pending.current.shift();
          if (interjection) {
            const temp: CouncilLine = { id: `tmp-${Date.now()}`, speaker: 'user', content: interjection, kind: 'message', created_at: new Date().toISOString() };
            append(temp);
            try {
              const r = await councilTurn({ sessionId: sid, name: roomName, members: seatedOf(membersRef.current), muted: mutedRef.current, message: interjection });
              setLines((p) => p.map((l) => (l.id === temp.id ? r.line : l)));
            } catch {
              /* keep the local line */
            }
          }
          const sp = order[turn % order.length];
          if (mutedRef.current.includes(sp)) continue;
          setThinking(sp);
          try {
            const since = new Date(Date.now() - 5000).toISOString();
            const r = await councilSpeak(sid, sp);
            if (!alive.current) return;
            if (r.wrote) wroteData({ sessionId: sid, speaker: sp, since });
            if (r.line) {
              passes = 0;
              append(r.line);
              if (conference) councilAudio.enqueue({ id: r.line.id, speaker: r.line.speaker, text: r.line.content });
              if (done(r.line)) break;
            } else if (++passes >= 2) break;
          } catch (e) {
            note(`${seatName(sp)} could not answer (${e instanceof Error ? e.message : 'error'}).`);
            if (++passes >= 2) break;
          }
        }
      } finally {
        if (alive.current) {
          setThinking(null);
          setBusy(false);
          const next = pending.current.shift();
          if (next) void sendRef.current(next);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conference, roomName],
  );
  const stop = useCallback(() => {
    stopRef.current = true;
    councilAudio.stop();
  }, []);

  // The topic SyQi was given ("council mode about my week") opens the session.
  const topicSent = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!active || !topic || topicSent.current === topic || settingsCol.loading) return;
    topicSent.current = topic;
    void sendRef.current(topic);
  }, [active, topic, settingsCol.loading]);

  const ear = useCouncilEar({ onUtterance: (txt) => void sendRef.current(txt), hold: !!play.id });
  const earRef = useRef(ear);
  earRef.current = ear;
  // Hands-free mic follows the room: on when it opens (if the setting says so), off when it closes.
  useEffect(() => {
    if (settingsCol.loading) return;
    if (active && settings.voiceInput) void earRef.current.start();
    if (!active) earRef.current.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, settingsCol.loading]);

  const persist = (m: string[], mu: string[]) => {
    if (sessionId) void saveCouncilRoster(sessionId, seatedOf(m), mu);
  };
  /** Put anyone (a deity or SyQi) on podium `slot`; they leave any other podium they stood on. */
  const seat = (id: string, slot: number) => {
    const m = padSlots(members).map((x) => (x === id ? '' : x));
    m[slot] = id;
    const mu = muted.filter((x) => m.includes(x));
    setMembers(m);
    setMuted(mu);
    persist(m, mu);
    note(`${seatName(id)} took a seat.`);
  };
  const unseat = (id: string) => {
    const m = padSlots(members).map((x) => (x === id ? '' : x));
    const mu = muted.filter((x) => x !== id);
    setMembers(m);
    setMuted(mu);
    persist(m, mu);
    note(`${seatName(id)} left the council.`);
  };
  const toggleMute = (id: string) => {
    const mu = muted.includes(id) ? muted.filter((x) => x !== id) : [...muted, id];
    setMuted(mu);
    persist(members, mu);
  };
  const nudge = async (id: string) => {
    if (!sessionId) {
      note('Say something to the council first, then nudge.');
      return;
    }
    if (busy) return;
    setBusy(true);
    await runSpeakers(sessionId, [id]);
    if (alive.current) setBusy(false);
  };
  const toggleConference = () => {
    const next = !conference;
    setConference(next);
    if (!next) councilAudio.stop();
    settingsCol.setValue({ ...settings, autoSpeak: next });
  };
  const refreshSessions = async () => {
    if (userId) setSessions(await listCouncilSessions(userId));
  };
  const resume = async (s: CouncilSession) => {
    councilAudio.stop();
    setSessionId(s.id);
    setName(s.title);
    setStatus(s.status ?? 'active');
    setMembers(padSlots(s.members));
    setMuted(s.muted ?? []);
    setLines(await loadCouncilLines(s.id));
  };
  const fresh = () => {
    councilAudio.stop();
    setSessionId(undefined);
    setName('');
    setStatus('active');
    setLines([]);
    setMuted([]);
    setMembers(padSlots(settings.defaultMembers));
  };
  /** Bothread "Start room": a named council, empty podiums, before the first line. */
  const start = async (title: string) => {
    if (!userId) return;
    councilAudio.stop();
    const s = await createCouncilSession(userId, title, []);
    setSessionId(s.id);
    setName(s.title);
    setStatus('active');
    setMuted([]);
    setMembers(padSlots([]));
    setLines([]);
    void refreshSessions();
  };
  const rename = async (title: string) => {
    const t = title.trim();
    if (!t || !sessionId) return;
    setName(t);
    await updateCouncilSession(sessionId, { title: t });
    void refreshSessions();
  };
  const setRoomStatus = async (next: CouncilStatus) => {
    if (!sessionId) return;
    setStatus(next);
    if (next !== 'active') councilAudio.stop();
    await updateCouncilSession(sessionId, { status: next });
    note(next === 'paused' ? 'Council paused. Deities wait.' : next === 'closed' ? 'Council closed.' : 'Council resumed.');
    void refreshSessions();
  };
  const remove = async (id: string) => {
    await deleteCouncilSession(id);
    setSessions((p) => p.filter((x) => x.id !== id));
    if (id === sessionId) fresh();
  };
  const playLine = (l: CouncilLine) => (play.id === l.id ? void councilAudio.togglePause() : councilAudio.playNow({ id: l.id, speaker: l.speaker, text: l.content }));

  return {
    settings,
    sessionId,
    name,
    status,
    start,
    rename,
    setRoomStatus,
    members,
    muted,
    lines,
    thinking,
    busy,
    speakingId,
    play,
    conference,
    ear,
    sessions,
    send,
    converse,
    stop,
    seat,
    unseat,
    toggleMute,
    nudge,
    toggleConference,
    refreshSessions,
    resume,
    fresh,
    remove,
    playLine,
  };
}

export type Council = ReturnType<typeof useCouncil>;
